import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription, catchError, combineLatest, firstValueFrom, of, tap, throwError } from 'rxjs';

import { CoreApiService, queryAll } from '../../../../core-api.service';
import { LoggingService } from '../../../../logging.service';
import { NotificationService } from '../../../../notification.service';
import { ContainerType } from '../../../../shared/enums/container-type.enum';
import { Form, FormInput } from '../../../../shared/models/form.model';
import { ReadTaskProtocol, ReadTaskProtocolInput } from '../../../../shared/models/read-task-protocol.model';
import { ViewerConfig, ViewerConfigInput } from '../../../../shared/models/viewer-config.model';
import { ViewerService } from '../../../../viewers/viewer.service';

enum ProtocolResourceEnum {
  Form = 'form',
  ViewerConfig = 'viewerConfig',
}

type ProtocolResource = ProtocolResourceEnum | ProtocolResourceEnum[keyof ProtocolResourceEnum];

@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'viewer-protocol-editor-dialog',
  },
  templateUrl: './viewer-protocol-editor-dialog.component.html',
  styleUrls: ['./viewer-protocol-editor-dialog.component.scss'],
})
export class ViewerProtocolEditorDialogComponent implements OnInit, OnDestroy {
  viewerForm: Form
  viewerConfig: ViewerConfig
  loading = false
  formGroup: FormGroup;
  existingProtocolLabels: string[] = []
  subscriptions = {
    init: Subscription.EMPTY,
    save: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { protocol: ReadTaskProtocol, projectId: string },
    private dialogRef: MatDialogRef<ViewerProtocolEditorDialogComponent>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private viewerService: ViewerService,
  ) {}

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      label: new FormControl(this.data?.protocol?.label || '', [Validators.required, this.uniqueLabel()]),
      description: new FormControl(this.data?.protocol?.description || '', [Validators.required]),
      form_id: new FormControl(this.data?.protocol?.form_id || ''),
      viewer_config_id: new FormControl(this.data?.protocol?.viewer_config_id || ''),
    });

    this.subscriptions.init = combineLatest([
      this.data.protocol ? this.coreApi.forms.get(this.data.protocol.form_id) : of(null),
      this.data.protocol ? this.coreApi.viewerConfigs.get(this.data.protocol.viewer_config_id) : of(null),
      queryAll(() => this.coreApi.readTaskProtocols.query({ 'parents.project': this.data.projectId })),
    ]).subscribe({
      next: ([form, viewerConfig, existingProtocols]) => {
        this.loading = false;
        this.viewerForm = form;
        this.viewerConfig = viewerConfig;
        this.existingProtocolLabels = existingProtocols.filter(protocol => protocol._id !== this.data.protocol?._id).map(p => p.label);
        this.formGroup.get('label').updateValueAndValidity(); // trigger uniqueLabel validator
      },
      error: error => {
        this.logger.error(error);
      },
    })
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    let protocol$;
    const protocolInput: ReadTaskProtocolInput = {
      ...this.formGroup.value,
      parent: {
        type: ContainerType.Project,
        id: this.data.projectId,
      },
    };
    if (!this.data?.protocol?._id) {
      protocol$ = this.coreApi.readTaskProtocols.post(protocolInput);
    } else {
      protocol$ = this.coreApi.readTaskProtocols.put(this.data.protocol._id, protocolInput);
    }
    this.subscriptions.save = protocol$.subscribe({
      next: protocol => {
        const notificationKey = this.data?.protocol?._id ? 'viewerProtocols.edit.success' : 'viewerProtocols.create.success';
        this.notifications.success(notificationKey);
        this.dialogRef.close(protocol);
      },
      error: error => {
        this.logger.error(error);
        const notificationKey = this.data?.protocol?._id ? 'viewerProtocols.edit.error' : 'viewerProtocols.create.error';
        this.notifications.error(notificationKey);
      },
    });
  }

  selectFile(resource: ProtocolResource, event: Event): void {
    const file = (event.target as HTMLInputElement).files[0];
    file.text().then(contents => {
      this.createNewResource(resource, contents);
    });
  }

  createNewResource(resource: ProtocolResource, contents?: string): Promise<Form | ViewerConfig> {
    let parsedContents = {};
    const notificationKey = resource === ProtocolResourceEnum.Form ? 'viewerProtocols.formUpload' : 'viewerProtocols.viewerConfigUpload';
    try {
      parsedContents = JSON.parse(contents || '{}');
    }
    catch (error) {
      this.logger.error(error);
      this.notifications.error(`${notificationKey}.errorJSON`);
      return Promise.reject(error);
    }
    const newForm: FormInput = {
      viewer: 'OHIF',
      parent: { id: this.data.projectId, type: ContainerType.Project },
      form: parsedContents as FormInput['form'],
    };
    const newConfig: ViewerConfigInput = {
      name: 'UI Generated Viewer Config',
      config: parsedContents as ViewerConfigInput['config'],
    };
    if (resource === ProtocolResourceEnum.Form) {
      return firstValueFrom(this.coreApi.forms.post(newForm).pipe(
        catchError(error => {
          if (error.status === 422) {
            // I am consiously not logging this error because users are going to upload bad JSON
            this.notifications.error(`${notificationKey}.errorValidation`);
          } else {
            this.logger.error(error);
            this.notifications.error(`${notificationKey}.error`);
          }
          throw error;
        }),
        tap((form: Form) => {
          this.viewerForm = form;
          this.formGroup.get('form_id').setValue(form._id);
        }),
      ));
    }
    else if (resource === ProtocolResourceEnum.ViewerConfig) {
      return firstValueFrom(this.coreApi.viewerConfigs.post(newConfig).pipe(
        catchError(error => {
          if (error.status === 422) {
            // I am consiously not logging this error because users are going to upload bad JSON
            this.notifications.error(`${notificationKey}.errorValidation`);
          } else {
            this.logger.error(error);
            this.notifications.error(`${notificationKey}.error`);
          }
          throw error;
        }),
        tap((config: ViewerConfig) => {
          this.viewerConfig = config;
          this.formGroup.get('viewer_config_id').setValue(config._id);
        }),
      ));
    }

    return Promise.reject();
  }

  async editResource(resource: ProtocolResource): Promise<void> {
    if ((resource === ProtocolResourceEnum.Form && !this.viewerForm?._id) ||
      (resource === ProtocolResourceEnum.ViewerConfig && !this.viewerConfig?._id)) {
      await this.createNewResource(resource);
    }
    // This callback executes when the user clicks the save button in the editor, the editor stays open after saving to display notifications
    const saveCallback = (newValue: string): Observable<Form|ViewerConfig> => {
      let parsedValue;
      try {
        parsedValue = JSON.parse(newValue);
      }
      catch (error) {
        return throwError(() => new Error('Invalid JSON'));
      }
      const updatedForm = {
        ...this.viewerForm,
        form: parsedValue,
      };
      const updatedConfig = {
        ...this.viewerConfig,
        config: parsedValue,
      };
      if (resource === ProtocolResourceEnum.Form) {
        this.viewerForm = updatedForm;
        return this.coreApi.forms.put(this.viewerForm._id, updatedForm);
      }
      this.viewerConfig = updatedConfig;
      return this.coreApi.viewerConfigs.put(this.viewerConfig._id, updatedConfig);
    };

    const value = (resource === ProtocolResourceEnum.Form ? this.viewerForm?.form : this.viewerConfig?.config) || {};
    const fileName = resource === ProtocolResourceEnum.Form ? 'ViewerForm.json' : 'ViewerConfig.json';
    this.viewerService.editTextInMonacoViewer({
      value: JSON.stringify(value),
      fileType: 'json',
      fileName,
      autoFormat: true,
      readOnly: false,
    }, saveCallback).subscribe({
      next: () => {
        // The editor closed; nothing to do as saving happens in the callback
      },
      error: error => {
        this.logger.error(error);
      },
    });
  }

  private uniqueLabel = () => (control: FormControl) => {
    return this.existingProtocolLabels.includes(control.value) ? { duplicateLabel: true } : null;
  }
}
